import { Navigate, type RouteObject } from 'react-router-dom';
import { RoleGuard } from '@/shared/guards/RoleGuard';
import { SALES_ROLES } from '@/shared/guards/roles';
import { SalesPage } from './pages/SalesPage';
import { TransactionDetailPage } from './pages/TransactionDetailPage';
import { ContractsPage } from './pages/ContractsPage';
import { ContractDetailPage } from './pages/ContractDetailPage';
import { BillingAgreementDetailPage } from './pages/BillingAgreementDetailPage';
import { DebtArrangementDetailPage } from './pages/DebtArrangementDetailPage';
import { OfferDetailPage } from './pages/OfferDetailPage';
import { OffersPage } from './pages/OffersPage';
import { SaleWebhookInboxPage } from './pages/SaleWebhookInboxPage';

/**
 * Sub-árvore de rotas do módulo de Vendas (Sales Admin). Gateado por
 * `SALES_ROLES` — espelha o `@Roles(...SALES_READ_ROLES)` na classe do
 * `SalesAdminReadController` no monolith. Quem passa neste guard também executa
 * as ações: o `SalesAdminController` exige a mesma alçada desde que o eixo
 * `salesViewer` × `salesEditor` foi removido do catálogo. O gating do front é só
 * UX; o back é a autoridade real.
 */
export const salesRoutes: RouteObject[] = [
  {
    element: <RoleGuard roles={SALES_ROLES} />,
    children: [
      {
        path: 'sales',
        children: [
          { index: true, element: <SalesPage /> },
          // `:tab?` opcional: a aba vive na URL (compartilhável, sobrevive a
          // refresh) e trocar de aba não remonta a página — só muda o param.
          {
            path: ':transactionId/:tab?',
            element: <TransactionDetailPage />,
          },
        ],
      },
      {
        path: 'contracts',
        children: [
          { index: true, element: <ContractsPage /> },
          { path: ':contractId/:tab?', element: <ContractDetailPage /> },
        ],
      },
      {
        // Fora de `contracts/:id` de propósito: uma BA substituída não pertence
        // mais ao contrato "atual", e a página é alcançada pelo id da BA, que é
        // link em toda tela do Sales.
        path: 'billing-agreements',
        children: [
          {
            path: ':billingAgreementId/:tab?',
            element: <BillingAgreementDetailPage />,
          },
        ],
      },
      {
        // Alcançada pelo id na aba "Offer & Renegociações", não pelo menu.
        path: 'debt-arrangements',
        children: [
          {
            path: ':debtArrangementId/:tab?',
            element: <DebtArrangementDetailPage />,
          },
        ],
      },
      {
        // A lista entra pelo menu; o detalhe também é alcançado por link
        // (`EntityLink kind="offer"`) de toda tela do Sales.
        path: 'offers',
        children: [
          { index: true, element: <OffersPage /> },
          { path: ':offerId/:tab?', element: <OfferDetailPage /> },
        ],
      },
      { path: 'sales/webhook-inbox', element: <SaleWebhookInboxPage /> },
      {
        // A fila virou alerta na Central de Pendências; o endereço antigo
        // segue vivo em favorito e em e-mail já enviado.
        path: 'sales/inconsistencies',
        element: <Navigate to="/pending-alerts" replace />,
      },
    ],
  },
];
